
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Edit } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import "react-circular-progressbar/dist/styles.css";
import { Tables } from "@/integrations/supabase/types";
import { useNavigate, useParams } from "react-router-dom";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileAvatar } from "@/components/profile/ProfileAvatar";
import { ProfileInfo } from "@/components/profile/ProfileInfo";
import { VacationHistory } from "@/components/profile/VacationHistory";
import { calculateBusinessDays, getCurrentYearDateRange } from "@/utils/dateUtils";
import { toast } from "sonner";
import { isPostgrestError, formatSupabaseError } from "@/utils/supabaseHelpers";
import { uploadAvatar } from "@/utils/storageUtils";

type Profile = Tables<"profiles">;
type VacationRequest = Tables<"vacation_requests">;

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userId } = useParams();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        toast("Please log in to view this page");
      }
    };
    
    checkAuth();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/login");
      toast.success("Signed out successfully");
    } catch (error) {
      toast.error("Error signing out");
    }
  };

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      if (userId) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", userId)
          .single();
        
        if (error) throw error;
        return data as Profile;
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("No user found");
        
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();
        
        if (error) throw error;
        return data as Profile;
      }
    },
  });

  const { data: approvedVacations } = useQuery({
    queryKey: ["approved_vacations", profile?.id, new Date().getFullYear()],
    queryFn: async () => {
      if (!profile?.id) throw new Error("No profile ID");
      
      const { start, end } = getCurrentYearDateRange();
      
      const { data, error } = await supabase
        .from("vacation_requests")
        .select("*")
        .eq("user_id", profile.id)
        .eq("status", "approved")
        .gte("start_date", start)
        .lte("start_date", end);
      
      if (error) throw error;
      return data as VacationRequest[];
    },
    enabled: !!profile?.id,
  });

  const approvedDays = approvedVacations?.reduce((total, vacation) => {
    const start = new Date(vacation.start_date);
    const end = new Date(vacation.end_date);
    const days = calculateBusinessDays(start, end);
    return total + (vacation.is_half_day ? days * 0.5 : days);
  }, 0) || 0;

  const remainingVacationDays = Math.floor((profile?.vacation_days || 0) - approvedDays);

  const { data: vacationRequests } = useQuery({
    queryKey: ["vacation_requests", profile?.id],
    queryFn: async () => {
      if (!profile?.id) throw new Error("No profile ID");
      
      const { data, error } = await supabase
        .from("vacation_requests")
        .select("*")
        .eq("user_id", profile.id);
      
      if (error) throw error;
      return data as VacationRequest[];
    },
    enabled: !!profile?.id,
  });

  const updateProfile = useMutation({
    mutationFn: async (updatedData: { full_name: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from("profiles")
        .update(updatedData)
        .eq("id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      setIsEditing(false);
      toast.success("Profile updated successfully");
    },
    onError: (error) => {
      console.error("Profile update error:", error);
      toast.error("Failed to update profile");
    },
  });

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      toast.error("No file selected");
      return;
    }
    
    try {
      setIsUploadingAvatar(true);
      toast("Uploading your avatar...");
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("No user found");
        return;
      }
      
      // Upload avatar - note bucket must exist in Supabase dashboard
      console.log("Uploading avatar for user:", user.id);
      const fileName = await uploadAvatar(file, user.id);
      
      if (!fileName) {
        toast.error("Failed to upload avatar");
        return;
      }
      
      // Invalidate queries to refresh profile data
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      
      toast.success("Avatar uploaded successfully");
    } catch (error) {
      console.error('Avatar upload error:', error);
      const errorMessage = isPostgrestError(error) 
        ? formatSupabaseError(error) 
        : 'Failed to upload avatar';
      toast.error(errorMessage);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    updateProfile.mutate({ full_name: formData.get("full_name") as string });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen" style={{ 
      background: 'linear-gradient(109.6deg, rgba(223,234,247,1) 11.2%, rgba(244,248,252,1) 91.1%)'
    }}>
      <ProfileHeader onSignOut={handleSignOut} />

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-2xl font-bold">Personal Information</CardTitle>
            {!userId && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setIsEditing(!isEditing)}
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center mb-6">
              <div className="relative">
                <ProfileAvatar 
                  profile={profile}
                  remainingVacationDays={remainingVacationDays}
                  isOwnProfile={!userId}
                  onAvatarUpload={handleAvatarUpload}
                />
              </div>
            </div>

            <ProfileInfo 
              profile={profile}
              isEditing={isEditing}
              isOwnProfile={!userId}
              onEdit={() => setIsEditing(!isEditing)}
              onSave={handleSave}
              isPending={updateProfile.isPending}
            />
          </CardContent>
        </Card>

        <VacationHistory vacationRequests={vacationRequests} />
      </div>
    </div>
  );
};

export default Profile;
